// SEO Component for dynamic meta tag management
// Uses react-helmet-async for page-specific SEO

import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component for managing page-specific meta tags
 * 
 * @param {string} title - Page title (will append "| Matax Express" if not included)
 * @param {string} description - Meta description for the page
 * @param {string} keywords - Comma-separated keywords
 * @param {string} image - OG image URL (defaults to /og-image.png)
 * @param {string} url - Canonical URL for the page
 * @param {string} type - OG type (website, article, product)
 * @param {boolean} noindex - If true, adds noindex meta tag
 * @param {object} structuredData - JSON-LD structured data object
 */
const SEO = ({
    title = "Matax Express | Wholesale Grocery & Fresh Produce Supplier Toronto",
    description = "Premium wholesale fruits, vegetables & grocery delivered to your business 7 days a week. Serving Toronto & GTA.",
    keywords = "wholesale grocery, Toronto produce, fresh vegetables, fruit supplier",
    image = "/og-image.png",
    url = "https://mataxexpress.ca",
    type = "website",
    noindex = false,
    structuredData = null
}) => {
    // Append branding if not already included
    const fullTitle = title.includes("Matax") ? title : `${title} | Matax Express`;

    // Ensure image URL is absolute
    const absoluteImage = image.startsWith('http') ? image : `https://mataxexpress.ca${image}`;

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            <link rel="canonical" href={url} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={absoluteImage} />
            <meta property="og:site_name" content="Matax Express" />
            <meta property="og:locale" content="en_CA" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={url} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={absoluteImage} />

            {/* Structured Data */}
            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;
